/**
 * The surfaces every page is built from.
 *
 * These are the class recipes the `/assemblies` redesign settled on, lifted out of that route so
 * the rest of the app can reach for the same card, the same icon tile, the same chip — rather than
 * each page re-deriving a slightly different border radius and a slightly different gray.
 *
 * They are plain strings on purpose: a caller appends its own layout classes (`p-6`, `flex`, a
 * width) without a helper, and Tailwind's scanner still sees every literal at build time.
 */

/** The one card: a full border all the way around, 16 px radius, white. */
export const CARD = 'rounded-2xl border border-gray-200 bg-white';

/** The card with its own padding, for panels that hold prose or a form rather than a table. */
export const CARD_PADDED = `${CARD} p-6`;

/**
 * The small square behind a leading icon — panel headings, list rows, empty states.
 *
 * 8 px radius inside a 16 px card: the nested-radius step for a 8 px gap.
 */
export const ICON_TILE = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700';

/** The small all-caps label that names a group of fields without competing with the heading. */
export const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wider text-gray-400';

/** The heading of a card or panel — one step below the page `h1`. */
export const PANEL_TITLE = 'text-lg font-semibold text-gray-900';

/**
 * File drop target, idle.
 *
 * 12 px radius: the step below a 16 px card when the gap between them is under 32 px. Pair it
 * with layout classes of your own — `flex w-full flex-col items-center gap-2 px-6 py-10` is the
 * shape the import dialog uses.
 */
export const DROP_ZONE = 'rounded-xl border-2 border-dashed border-gray-300 bg-white hover:border-primary';

/** The same drop target while a file is over it. */
export const DROP_ZONE_ACTIVE = 'rounded-xl border-2 border-dashed border-primary bg-primary/5';

/** A read-only pill: a tag, a count, a resolved value. Not a control — see `ActionButton` for that. */
export const CHIP = 'inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700';
